import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Briefcase,
  Globe,
  Laptop,
  Luggage,
  PackageOpen,
  PlaneTakeoff,
  ShieldCheck,
} from "lucide-react";

export const bagsLuggagePrompts: CategoryPrompt[] = [
  {
    icon: <PlaneTakeoff />,
    title: "Travel Use Case Landing Pages",
    description:
      "Create a page for a specific travel scenario targeting high-intent searches, with capacity breakdowns, relevant features, and testimonials from similar travelers.",
    prompt: `Build a travel scenario-specific landing page that targets high-intent searches and converts travelers needing luggage for specific trip types.

**TRAVEL SCENARIO:** e.g., "Best Carry-On for European Backpacking" OR "Family Beach Vacation Luggage" OR "Business Travel Essentials"

**TARGET TRAVELER:** e.g., Solo backpackers, family vacationers, business travelers, adventure seekers

**Trip Context:** Choose one:
- Weekend getaway (2-4 days)
- Week-long trip (5-10 days)
- Extended travel (2+ weeks)

**Tone & Style:**
- Practical travel advisor
- Experience-focused not feature-focused
- Problem-solving and reassuring
- Wanderlust-inspiring but grounded

**CTA Guidelines:** Each CTA should:
- Reference the specific scenario ("Pack for Europe")
- Use traveler language ("Ready for Adventure")
- Include capacity context ("Fits 7 Days Easy")
- Emphasize travel benefits ("TSA-Compliant Size")

**Conversion Tactics Use:**
- Scenario-specific packing lists
- Capacity visualizations (X outfits, Y shoes)
- Airline compliance badges (carry-on approved)
- Traveler testimonials from same scenario
- Trip duration matching ("Perfect for 5-7 day trips")
- Bundle deals for complete travel sets

**Key Elements to Include:**
- **Hero Section:** Scenario headline, lifestyle image (traveler in scenario), primary product recommendation, CTA
- **Scenario Description:** Brief overview of this travel type and luggage needs
- **Capacity Breakdown:** "What fits: 5-7 outfits, 3 pairs shoes, toiletries, tech" with visual
- **Top Recommendation (Hero Product):** Best bag for this scenario with why-it-works explanation
- **Key Features for Scenario:** 4-6 features relevant to this trip type (size, durability, organization, weight)
- **Airline Compliance:** Size specs + "Fits overhead on [airlines]" badges
- **Packing Guide:** Suggested items for this scenario with capacity visual
- **Alternative Options:** 2-3 other bags at different price points or sizes
- **Traveler Testimonials:** 3-5 reviews from people who used for same trip type
- **Comparison Chart:** 3-5 bag options compared on scenario-relevant specs
- **Travel Tips Section:** Pro tips for this specific scenario
- **Bundle Suggestion:** Complete travel set (bag + accessories for scenario)
- **Size Selector:** If offering multiple sizes, guide on which for trip length
- **CTAs:** Hero, after capacity breakdown, with alternatives, bundle section (5-6 total)
- **FAQ:** 4-5 questions (Airline rules? Durability for X? Carry-on vs checked? Warranty?)

**Goal:** Convert high-intent travelers searching for scenario-specific luggage by demonstrating exact capacity, relevant features, and social proof from similar travelers that this bag perfectly fits their trip needs.`,
  },
  {
    icon: <Luggage />,
    title: "Durability Problem-Solution Listicle",
    description:
      "Create a listicle addressing common product failures, explaining each failure point and linking to your products that solve it with technical specs and CTAs.",
    prompt: `Build a durability-focused listicle that addresses common luggage failures and positions your products as the engineered solutions.

**TOPIC:** e.g., "7 Reasons Your Luggage Always Breaks (And How to Avoid It)" OR "5 Weak Points That Ruin Cheap Suitcases"

**PRODUCTS FEATURED:** e.g., "Our reinforced carry-on solves problem #3" OR "Our hardshell fixes issue #5"

**Reader Type:** Choose one:
- Frustrated with broken luggage (ready to replace)
- Researching before purchase (avoiding mistakes)
- Frequent travelers (need reliability)

**Tone & Style:**
- Empathetic to luggage frustrations
- Technical but accessible
- Quality-focused not price-focused
- "We engineered this better" confidence

**CTA Guidelines:** Each CTA should:
- Reference the problem solved ("No More Broken Zippers")
- Use durability language ("Built to Last")
- Include warranty/testing proof ("Lifetime Guarantee")
- Emphasize engineering solution ("Reinforced Design")

**Conversion Tactics Use:**
- Failure point diagrams with visuals
- Technical spec comparisons (cheap vs. quality)
- Stress test results and certifications
- Customer stories of product longevity
- Warranty as proof of confidence
- Side-by-side material comparisons

**Key Elements to Include:**
- **Hook:** Relatable failure moment ("Wheel breaks off at airport security...")
- **List Items (5-8 failure points):** Numbered problem → why it fails → your solution
- **Problem Structure Per Item:**
  - Failure headline: "Problem #X: [Specific breaking point]"
  - Why this fails: Technical explanation
  - Cost of failure: Inconvenience + replacement cost
  - Your solution: Product with specific fix
  - Technical specs: Material, construction, testing
  - Visual: Diagram or photo showing reinforcement
  - Customer quote: Durability testimonial
- **Product Callouts:** Technical feature highlights with "Shop Solution" CTA
- **Comparison Boxes:** Cheap luggage vs. your engineered solution (2-3 throughout)
- **Stress Test Results:** "Survived 10,000 drops" or certification proof
- **Warranty Section:** Lifetime or extended warranty as confidence signal
- **CTAs:** After problem #2, after problem #5, conclusion (5-7 total)
- **FAQ:** 3-4 questions (Warranty coverage? Testing standards? Repairs? Material care?)

**Goal:** Convert frustrated travelers and quality-conscious buyers by exposing common failure points and demonstrating your products' superior engineering, materials, and testing that eliminate these problems.`,
  },
  {
    icon: <Laptop />,
    title: "Traveler Profile Quiz",
    description:
      "Build a quiz asking about travel style and needs, then show personalized recommendations with explanations, gated behind email capture.",
    prompt: `Build a traveler profile quiz that matches customers to ideal luggage based on their travel habits and preferences, gated with email capture.

**QUIZ FOCUS:** e.g., "Find Your Perfect Travel Bag" OR "Which Luggage Matches Your Travel Style?" OR "Your Ideal Travel Setup"

**PRODUCT RANGE:** e.g., Carry-ons, checked bags, duffels, backpacks (10-20 products to match from)

**Quiz Taker Type:** Choose one:
- New travelers (building first setup)
- Upgrading travelers (replacing old luggage)
- Frequent flyers (optimizing their system)

**Tone & Style:**
- Travel-savvy advisor
- Personality-driven not clinical
- Aspirational and adventure-focused
- Practical recommendations

**Quiz Question Guidelines:** Each question should:
- Use travel lifestyle language
- Include visual answer options (icons or travel scenes)
- Build toward specific product attributes
- Feel like fun personality test
- 5-7 total questions maximum

**Email Gate Strategy:**
- Place after question #4 (mid-commitment)
- Frame as "Get your personalized travel setup"
- Offer incentive: "Unlock matches + 15% travel prep discount"
- Single field: email only
- Trust line: "Join 50,000+ travelers"

**Results Page Elements:**
- **Personalized Headline:** "Based on your [travel style], here's your perfect setup"
- **Traveler Type Badge:** "You're a [Minimalist Adventurer/Luxury Weekender/etc.]"
- **Primary Recommendation:** Top luggage pick with detailed match reasoning
- **Complete Travel Kit:** 3-5 complementary pieces as bundle
- **Alternative Options:** 2-3 other choices at different capacities or styles
- **Product Cards Include:** Image, price, capacity, key feature for their style, reviews, CTA
- **Match Reasoning:** "You said [answer], so this [feature] is perfect because..."
- **Packing Guide:** Suggested setup for their travel type
- **Bundle Discount:** "Complete kit saves $X"
- **CTAs on Each Product:** "Shop Your Match" with urgency
- **Retake Option:** "Travel style changed? Retake quiz"

**Goal:** Convert overwhelmed luggage shoppers into confident buyers by providing personalized recommendations based on their actual travel patterns, removing decision paralysis through expert matching.`,
  },
  {
    icon: <Briefcase />,
    title: "Warranty & Guarantee Page",
    description:
      "Build a page explaining my warranty program with clear terms, customer success stories, and testimonials from customers who used it.",
    prompt: `Build a warranty page that turns your guarantee into a competitive advantage by showcasing transparent terms and real claim success stories.

**GUARANTEE TYPE:** e.g., "Lifetime Warranty" OR "10-Year Durability Promise" OR "No-Questions Replacement Policy"

**COVERAGE SCOPE:** e.g., Manufacturing defects, wear-and-tear, travel damage, zipper/wheel failures

**Audience Type:** Choose one:
- Skeptical buyers (need reassurance before purchase)
- Frequent travelers (expect quality backing)
- Premium shoppers (warranty signals quality)

**Tone & Style:**
- Confident and straightforward
- No fine-print tricks
- Customer-first language
- Proud of standing behind products

**CTA Guidelines:** Each CTA should:
- Reference confidence ("Shop Risk-Free")
- Use guarantee as differentiator ("Lifetime Protected")
- Include ease messaging ("Easy Claims")
- Link to products ("See What's Covered")

**Conversion Tactics Use:**
- Real claim stories with photos
- Claim approval rate ("97% approved same-day")
- Before/after replacement photos
- Competitor warranty comparison
- Hassle-free process emphasis
- Time-based response guarantees

**Key Elements to Include:**
- **Hero Section:** Bold guarantee headline, trust badge, "Shop with Confidence" CTA
- **What's Covered:** Clear list (defects, failures, damage types)
- **How It Works:** 3-4 simple steps (Contact → Photo → Replacement)
- **Coverage Duration:** Lifetime/years timeline visual
- **Real Claim Stories (4-6):** Customer name, what broke, how handled, outcome, photos
- **Success Metrics:** "15,000+ claims honored" or "97% same-day approval"
- **What's NOT Covered:** Honest transparency (theft, airline loss, intentional damage)
- **Comparison Chart:** Your warranty vs. typical vs. competitors (3-5 rows)
- **Claim Process Details:** Contact methods, response time, shipping
- **Customer Testimonial Grid:** 6-10 short quotes about warranty experience
- **Product Coverage List:** Which products have which warranties
- **CTAs:** Hero, after stories, with product links (5-6 total)
- **FAQ:** 5-7 questions (Receipt needed? International? Used luggage? Process time?)

**Goal:** Convert hesitant shoppers into confident buyers by showcasing an industry-leading warranty backed by real customer stories that prove you actually honor it, turning guarantee from fine print into a major purchase motivator.`,
  },
  {
    icon: <ShieldCheck />,
    title: "Customer Travel Story Gallery",
    description:
      "Create a UGC gallery featuring customer-submitted photos with trip details and durability testimonials.",
    prompt: `Build a customer travel story gallery showcasing real travelers using your luggage worldwide to provide authentic social proof and inspire purchases.

**GALLERY FOCUS:** e.g., "Where Our Luggage Has Traveled" OR "Real Travelers, Real Adventures" OR "Your Journey, Our Bags"

**PHOTO CATEGORIES:** e.g., By destination, by bag type, by trip duration, by rating (4-5★ only)

**Gallery Visitor Type:** Choose one:
- Research-phase shoppers (need real-world validation)
- Adventure-inspired buyers (want aspirational proof)
- Durability skeptics (want evidence it survives travel)

**Tone & Style:**
- Adventurous and inspiring
- Authentic traveler community
- Wanderlust-inducing but genuine
- Celebration of real journeys

**CTA Guidelines:** Each CTA should:
- Use community language ("Join 20,000+ Travelers")
- Reference adventure ("Start Your Journey")
- Include social proof ("Trusted Worldwide")
- Link to featured products

**Conversion Tactics Use:**
- Filter by destination type (beaches, mountains, cities)
- Durability testimonials in captions
- Trip duration tags ("30-day backpacking trip")
- "Most traveled" product highlights
- Photo submission incentive (discount for next trip)
- Verified purchase badges

**Key Elements to Include:**
- **Hero Section:** Headline, photo grid preview, filter options, "Share Your Story" CTA
- **Filter Options:** By destination type, by product, by trip length, by rating, verified only
- **Photo Grid Layout:** Hover-to-expand, each shows traveler name, destination, product used, brief caption
- **Featured Travel Stories (3-5):** Large image, traveler name/location, full story (2-3 paragraphs), multiple photos, product featured, "Shop This Bag" CTA
- **Destination Map:** Interactive world map with photo pins showing global reach
- **Social Proof Metrics:** "Used in 127+ countries" or "50,000+ adventures"
- **Product-Specific Galleries:** Dedicated sections per bag with real-use photos
- **Durability Highlights:** Filter for "extreme travel" or "survival stories"
- **Photo Submission Section:** "Share your adventure & get 20% off" with upload interface
- **CTAs:** Top of page, with featured stories, product galleries (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Privacy? When featured? Incentive?)

**Goal:** Convert hesitant shoppers into confident buyers by showcasing authentic global adventures that prove your luggage performs in real-world travel conditions across every destination type and journey length.`,
  },
  {
    icon: <Globe />,
    title: "Free Shipping Threshold Campaign",
    description:
      "Create a page with progress bar showing proximity to free shipping and smart product recommendations to hit minimum spend.",
    prompt: `Build a cart or product page with dynamic free shipping progress bar and strategic upsells to push customers over the threshold.

**SHIPPING THRESHOLD:** e.g., "$75 for free shipping" OR "$100 for free express shipping"

**CURRENT CART VALUE:** e.g., "$52" OR "$68" OR "$25"

**Customer Stage:** Choose one:
- Cart page (reviewing items)
- Product page (first add)
- Checkout intercept (leaving without hitting threshold)

**Tone & Style:**
- Encouraging and helpful
- Value-focused (saving money motivation)
- Celebratory when threshold hit
- No pressure, just opportunity

**Progress Bar Guidelines:**
- Placement: Top of page, sticky on scroll
- Visual: Animated fill bar showing progress
- Copy: "Add $23 more for FREE SHIPPING!" or "You're $23 away!"
- Success: "🎉 You unlocked FREE SHIPPING!"
- Color: Red/yellow (far) → green (achieved)

**CTA Guidelines:** Each product CTA should:
- Show exact impact ("Add this = Free Shipping!")
- Use additive language ("Complete Your Order")
- Include quick-add (no leaving page)
- Display threshold math nearby

**Conversion Tactics Use:**
- Exact dollar gap messaging ("Just $18.50 more!")
- Perfect-fit product suggestions (items matching exact gap)
- Bundle recommendations exceeding by small amount
- Countdown urgency ("Offer ends today")
- Comparison savings ("$12 shipping vs. $15 product")
- Quick-add buttons (no cart navigation)

**Key Elements to Include:**
- **Progress Bar (Sticky):** Current amount, goal amount, gap remaining
- **Recommendation Headline:** "Add one of these to unlock free shipping:"
- **Product Grid:** 4-6 items strategically priced to hit/exceed threshold
- **Price Highlighting:** Show impact "$52 → $70" per item
- **Quick-Add CTAs:** One-click add without leaving page
- **Bestseller Badges:** "Most added to hit free shipping"
- **Category Filters:** "Under $25" or "Small Add-Ons" for browsing
- **Cart Summary (Sidebar):** Running total with live threshold progress
- **Success Celebration:** Confetti or checkmark when achieved
- **Secondary Benefit:** "Free shipping + arrives by [date]"

**Goal:** Increase average order value by motivating customers to add complementary products to reach free shipping threshold instead of paying delivery fees.`,
  },
  {
    icon: <PackageOpen />,
    title: "Lifestyle Advertorial",
    description:
      "Create an advertorial positioning my products as part of an aspirational lifestyle, addressing objections and outlining benefits.",
    prompt: `Build a lifestyle advertorial that positions your luggage as essential to an aspirational travel lifestyle while naturally addressing purchase objections.

**LIFESTYLE ANGLE:** e.g., "The Minimalist Traveler's Guide to Smarter Packing" OR "How Premium Luggage Changed My Travel Game" OR "Life as a Digital Nomad: What's Actually Worth It"

**PRODUCTS FEATURED:** e.g., "Our carry-on system" OR "Premium travel collection"

**Reader Type:** Choose one:
- Aspiring to travel lifestyle (inspired but hesitant)
- Current travelers (looking to upgrade experience)
- Status-conscious buyers (quality signals lifestyle)

**Tone & Style:**
- Aspirational but relatable
- Lifestyle-first, product-second
- Addresses "is it worth it?" internally
- Confident recommendations from experience

**CTA Guidelines:** Each CTA should:
- Reference lifestyle benefit ("Travel Like a Pro")
- Use transformation language ("Upgrade Your Experience")
- Include social proof ("Join 50,000+ Smart Travelers")
- Emphasize long-term value

**Conversion Tactics Use:**
- Cost-per-use justification
- Lifestyle transformation framing
- Before/after travel experience
- Investment vs. expense positioning
- Quality-of-life improvements
- Status and confidence benefits

**Key Elements to Include:**
- **Hook:** Relatable lifestyle frustration or aspiration (2-3 paragraphs)
- **The Realization:** Turning point moment (lifestyle shift or decision)
- **Why Quality Matters:** Experience-based argument for investment
- **Objection Handling:** Address price, necessity, alternatives naturally in story
- **Product Integration:** Naturally woven into lifestyle narrative (3-4 mentions)
- **Benefits as Lifestyle Gains:** Less stress, more confidence, better experiences
- **Cost Justification:** "I travel 12x/year, so $X/trip = $Y per use"
- **Visual Lifestyle Context:** Describe scenes where product enhanced experience
- **Product Callout Boxes:** 2-3 highlighted features tied to lifestyle benefits
- **Comparison to Alternatives:** "I tried cheaper options, here's what happened"
- **Social Proof:** "Others making this shift" testimonials
- **Long-term Value:** Durability = years of better travel
- **CTAs:** After realization, mid-story with product, conclusion (4-6 total)
- **FAQ:** 3-4 questions (Worth the price? How long lasts? Alternatives? Best for?)

**Goal:** Convert price-hesitant shoppers into confident buyers by framing premium luggage as a lifestyle investment that improves travel quality, reduces stress, and pays for itself through durability and enhanced experiences.`,
  },
];
